/**
 * Knoria — Menu Configuration (Vincent batch v8 / 2026-05-08)
 *
 * Architecture rôle-based selon proposition Eric (.docx 2026-05-07c) :
 *   4 piliers > sections > items
 *   - Général       (apprenants)
 *   - Concepteurs   (pédagogues / créateurs de contenu)
 *   - Admin         (gestionnaires d'org)
 *   - Plateforme Knoria (fondateur seulement)
 *
 * 6 rôles effectifs :
 *   - fondateur  → tout
 *   - admin      → Général + Concepteurs + Admin (pas Plateforme)
 *   - concepteur → Général + Concepteurs (pas Admin ni Plateforme)
 *   - avance     → Général + édition Dictionnaire
 *   - forme      → Général
 *   - nouveau    → Général moins {Parcours d'apprentissage, Scripts, Procédures, Certification, Classement, Conformité AMF}
 *
 * Chaque item peut avoir :
 *   - roles: string[]      → liste blanche explicite
 *   - comingSoon: true     → afficher grisé avec badge "Bientôt disponible"
 *   - exact: true          → NavLink end matching
 *   - adminOnly: true      → legacy flag, kept for compat
 */
import { canAccessAdmin, canAccessPlatform } from "../utils/permissions";

// Hierarchy of roles (highest first) — used for default `roles` lists
export const ROLE_FONDATEUR = "fondateur";
export const ROLE_ADMIN = "admin";
export const ROLE_CONCEPTEUR = "concepteur";
export const ROLE_AVANCE = "avance";
export const ROLE_FORME = "forme";
export const ROLE_NOUVEAU = "nouveau";

export const ALL_ROLES = [ROLE_FONDATEUR, ROLE_ADMIN, ROLE_CONCEPTEUR, ROLE_AVANCE, ROLE_FORME, ROLE_NOUVEAU];

// Helpers — role short-hands used in items below
const R_ALL = ALL_ROLES;
const R_FROM_FORME = [ROLE_FONDATEUR, ROLE_ADMIN, ROLE_CONCEPTEUR, ROLE_AVANCE, ROLE_FORME];
const R_FROM_AVANCE = [ROLE_FONDATEUR, ROLE_ADMIN, ROLE_CONCEPTEUR, ROLE_AVANCE];
const R_CONCEPTEUR = [ROLE_FONDATEUR, ROLE_ADMIN, ROLE_CONCEPTEUR];
const R_ADMIN = [ROLE_FONDATEUR, ROLE_ADMIN];
const R_FONDATEUR = [ROLE_FONDATEUR];

/**
 * Compute the user's effective role for menu gating.
 * Mapping:
 *   is_founder=true                                    → fondateur
 *   org_role ∈ {ceo, president, vp, admin}             → admin
 *   org_role === "concepteur"                          → concepteur
 *   access_level === "avance"                          → avance
 *   access_level === "intermediaire"                   → forme
 *   else                                                → nouveau
 */
export function getEffectiveRole(user) {
  if (!user) return ROLE_NOUVEAU;
  // Eric 2026-06-25 (iter118 B3) — migré vers les helpers canoniques.
  if (canAccessPlatform(user)) return ROLE_FONDATEUR;
  if (canAccessAdmin(user)) return ROLE_ADMIN;
  if (user.org_role === "concepteur") return ROLE_CONCEPTEUR;
  const lvl = user.access_level || "debutant";
  if (lvl === "avance") return ROLE_AVANCE;
  if (lvl === "intermediaire") return ROLE_FORME;
  return ROLE_NOUVEAU;
}

/**
 * Build the menu structure. Icons are passed in by the caller (so we don't
 * import @phosphor-icons/react here and bloat the bundle).
 *
 * @param {object} icons   - map of iconName → IconComponent
 * @param {(key:string)=>string} t - i18n function
 * @param {string} lang    - "fr" | "en"
 * @returns {Array} pillars
 */
export function buildMenuConfig(icons, t, lang) {
  return [
    // ────────────────────────────────────────────────────────────────
    {
      id: "general",
      label: lang === "en" ? "General" : "Général",
      roles: R_ALL,
      sections: [
        {
          id: "general-introduction",
          label: lang === "en" ? "Introduction" : "Introduction",
          hideLabel: true, // Vincent batch v8 (Eric 2026-05-08) — label "Introduction" inutile
          items: [
            // Vincent batch v8 (Eric 2026-05-08) — Tableau de bord toujours visible (alwaysVisible)
            // même quand la section Introduction est repliée. C'est l'item d'accueil principal.
            { path: "/dashboard", icon: icons.House, label: t("dashboard"), exact: true, roles: R_ALL, alwaysVisible: true },
          ],
        },
        {
          id: "general-apprentissage",
          label: lang === "en" ? "Learning" : "Apprentissage",
          // Vincent #162 (Eric 2026-05-22) — Ordre imposé :
          //   1. Initiation (chemin d'accueil pour Nouveau employé)
          //   2. Parcours d'intégration (custom par formateur/fondateur)
          //   3. Parcours d'apprentissage (custom par formateur/fondateur)
          //   4. Messagerie
          //   5. Chatbot
          //   + Quiz (outil pédagogique)
          items: [
            { path: "/dashboard/discovery", icon: icons.Compass, label: lang === "en" ? "Onboarding" : "Initiation", roles: R_ALL, highlight: true, highlightColor: "amber" },
            { path: "/dashboard/agora", icon: icons.ChatCircleDots, label: lang === "en" ? "Messaging" : "Messagerie", roles: R_ALL },
            { path: "/dashboard/chat", icon: icons.ChatCircle, label: lang === "en" ? "Chatbot" : "Chatbot", roles: R_ALL },
            { path: "/dashboard/quiz", icon: icons.Exam, label: t("quiz"), roles: R_ALL },
            // Vincent B877 (Eric 2026-02-14 SPEC STRICK PROD) — Page globale
            // « Mes favoris » (tous types + toutes vidéos confondues). Vient
            // en complément de l'onglet per-vidéo (B849). Visible pour tous
            // les rôles (chaque utilisateur voit UNIQUEMENT ses propres ♥).
            { path: "/dashboard/favorites", icon: icons.Heart, label: lang === "en" ? "My favorites" : "Mes favoris", roles: R_ALL },
            // Vincent B592 (Eric 2026-07-28 SPEC STRICK) — Ressources externes,
            // indexées par le chatbot mais exclues des quiz.
            { path: "/dashboard/external-resources", icon: icons.Link, label: lang === "en" ? "External resources" : "Ressources externes", roles: R_ALL },
          ],
        },
        // Vincent #174 round 4 — Nouveau titre majeur "Parcours" regroupant
        // Intégration / Apprentissage. Explorateur de fichiers DÉPLACÉ vers
        // Concepteurs > Structure de données (Vincent #192).
        {
          id: "general-parcours",
          label: lang === "en" ? "Pathways" : "Parcours",
          items: [
            { path: "/dashboard/onboarding", icon: icons.Path, label: lang === "en" ? "Onboarding pathway" : "Parcours d'intégration", roles: R_ALL },
            { path: "/dashboard/learning-path", icon: icons.Path, label: lang === "en" ? "Learning pathway" : "Parcours d'apprentissage", roles: R_FROM_FORME },
            // Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — 3e type de parcours
            // « Formations hebdomadaires » (pathway_type=weekly) traité comme les
            // 2 autres : mêmes règles d'accès (défaut = learning), même UI,
            // apparaît dans tous les menus « ajouter à un parcours ».
            { path: "/dashboard/weekly-trainings", icon: icons.Path, label: lang === "en" ? "Weekly trainings" : "Formations hebdomadaires", roles: R_FROM_FORME },
            // Vincent XLSX 2026-07-13 21h Row 472 (Eric 2026-07-14 SPEC STRICK) —
            // 3e entrée "Accès aux parcours" pour founder/admin/concepteur/employee_advanced
            { path: "/dashboard/pathway-access", icon: icons.UsersThree, label: lang === "en" ? "Pathway access" : "Accès aux parcours", roles: R_FROM_AVANCE },
          ],
        },
        // Vincent #177 (Eric 2026-05-27) — "Base de connaissances" PLACÉE ICI
        // entre les accordéons "Parcours" et "Connaissances", SANS accordéon
        // (hideLabel = pattern utilisé pour Tableau de bord). Au même niveau
        // hiérarchique que les autres items, ni au-dessus ni en accordéon.
        {
          id: "general-base-connaissance",
          label: lang === "en" ? "Knowledge base" : "Base de connaissances",
          hideLabel: true,
          items: [
            { path: "/dashboard/knowledge", icon: icons.Brain, label: lang === "en" ? "Knowledge base" : "Base de connaissances", roles: R_ALL, alwaysVisible: true },
          ],
        },
        {
          id: "general-connaissances",
          label: lang === "en" ? "Knowledge" : "Connaissances",
          // Vincent #174/#177 round 4 — "Base de connaissances" RETIRÉE d'ici (item
          // standalone sans accordéon ci-dessus). Cette section ne contient plus
          // que les vues filtrées (FAQ / Scripts / Procédures / Concepts / Objections).
          items: [
            // Vincent B702 (Eric 2026-02-11) — Palette unifiée : chaque item KB
            // (FAQ, Scripts, Procédures, Concepts, Objections, Synthèses) reçoit
            // la couleur exacte de son trait vertical dans la timeline vidéo,
            // via TYPE_COLORS (source unique de vérité importée dans
            // DashboardLayout.js). Le renderer applique `color` en inline
            // style ; l'onglet actif se signale par font-bold (comme dans
            // PanelTabsBar + KnowledgePage). "Dictionnaire" reste neutre
            // (jargon, pas un type de KU).
            // Vincent B826 (Eric 2026-02-13) — « Synthèses » placé AVANT
            // « FAQ » et visible pour TOUS les rôles (y compris nouveau
            // employé). Précédemment `R_FROM_FORME` (5 rôles), maintenant
            // `R_ALL` (6 rôles).
            // Vincent B828bis (Eric 2026-02-14) — Ordre FINAL de la section
            // « Connaissances » (haut → bas) demandé par Vincent :
            //   Synthèses · Dictionnaire · Concepts · FAQ · Procédures ·
            //   Scripts de vente · Objections.
            // NB : Dictionnaire remonté en 2e position (juste après Synthèses)
            // pour rester cohérent avec les onglets de la Base de connaissances
            // (Top 10 · Tous · Synthèses · Dictionnaire · Concepts · FAQ · …).
            { path: "/dashboard/knowledge?tab=synthesis", icon: icons.Note, label: lang === "en" ? "Syntheses" : "Synthèses", roles: R_ALL, colorType: "synthesis" },
            { path: "/dashboard/dictionary", icon: icons.BookOpen, label: t("dictionary"), roles: R_FROM_AVANCE },
            // Tâche 1443 (Eric 2026-10-02) — « Orthographe », rangée JUSTE SOUS
            // le Dictionnaire à la demande d'Eric : c'est la même famille
            // d'objets (le vocabulaire du cabinet).
            // ⚠️ `R_CONCEPTEUR` et non `R_FROM_AVANCE` : Eric a confirmé
            // l'exclusion des rôles avancé / intermédiaire / débutant **même en
            // lecture**. La vraie barrière est `require_designer` au serveur.
            { path: "/dashboard/orthographe", icon: icons.TextAa || icons.BookOpen, label: lang === "en" ? "Spelling" : "Orthographe", roles: R_CONCEPTEUR },
            { path: "/dashboard/knowledge?tab=concept", icon: icons.Lightbulb, label: lang === "en" ? "Concepts" : "Concepts", roles: R_FROM_FORME, colorType: "concept" },
            { path: "/dashboard/knowledge?tab=qa", icon: icons.Question, label: lang === "en" ? "FAQ" : "FAQ", roles: R_ALL, colorType: "qa" },
            // Vincent #243 batch FLHQ (Eric 2026-02-06) — Procédures redirige vers
            // l'onglet "Procédures" de la base de connaissances (#252 confirme cette
            // unification). Scripts de vente conserve sa propre page pour l'instant
            // (Vincent a flaggé #279 : risque de régression scripts si on bouge).
            { path: "/dashboard/knowledge?tab=procedure", icon: icons.ClipboardText, label: lang === "en" ? "Procedures" : "Procédures", roles: R_FROM_FORME, colorType: "procedure" },
            { path: "/dashboard/scripts", icon: icons.ChatCircle, label: lang === "en" ? "Sales Scripts" : "Scripts de vente", roles: R_FROM_FORME, colorType: "script" },
            { path: "/dashboard/knowledge?tab=objection", icon: icons.ShieldWarning, label: lang === "en" ? "Objections" : "Objections", roles: R_FROM_FORME, colorType: "objection" },
          ],
        },
        {
          id: "general-performance",
          label: lang === "en" ? "Performance" : "Performance",
          items: [
            // Certification : masqué pour Nouveau employé
            { path: "/dashboard/certifications", icon: icons.Certificate, label: t("certifications"), roles: R_FROM_FORME },
            // Classement : masqué pour Nouveau employé
            { path: "/dashboard/leaderboard", icon: icons.Trophy, label: t("leaderboard"), roles: R_FROM_FORME },
            // Conformité AMF : masqué pour Nouveau employé
            { path: "/dashboard/amf-compliance", icon: icons.ShieldCheck, label: t("amf_compliance"), roles: R_FROM_FORME },
          ],
        },
      ],
    },
    // ────────────────────────────────────────────────────────────────
    {
      id: "concepteurs",
      label: lang === "en" ? "Designers" : "Concepteurs",
      roles: R_CONCEPTEUR,
      sections: [
        {
          id: "concepteurs-introduction",
          label: lang === "en" ? "Introduction" : "Introduction",
          hideLabel: true,
          items: [
            // Vincent #186 (Eric 2026-05-27) — "Initiation Concepteur" RETIRÉE
            // d'ici, déplacée en 1er dans "Génération de connaissances"
            // (renommée en minuscule "Initiation concepteurs").
            { path: "/dashboard/agent", icon: icons.Robot, label: lang === "en" ? "Knoria Agent" : "Agent Knoria", roles: R_CONCEPTEUR, highlight: true, alwaysVisible: true },
            { path: "/dashboard/agent/drafts", icon: icons.MagicWand, label: lang === "en" ? "Quick Validation" : "Validation rapide", roles: R_CONCEPTEUR, alwaysVisible: true },
            // Vincent #397 (Eric 2026-06-29 SPEC STRICK) — "Validation swipe" RETIRÉE
            // (doublon fonctionnel avec "Validation rapide"). Le mode swipe reste
            // accessible via le toggle interne de la page si besoin.
            // Eric 2026-06-25 (iter126 P2) — Dashboard maturité KB.
            { path: "/dashboard/bilan-kb", icon: icons.ChartBar, label: lang === "en" ? "KB Health" : "Bilan KB", roles: R_CONCEPTEUR, alwaysVisible: true },
            // Vincent B982 (Eric 2026-02-26) — Embeddings & RAG DÉPLACÉ
            // vers la section « Structure de données » entre Validation
            // des données et Transcriptions (Vincent : plus logique
            // groupé avec les diagnostics de la KB).
          ],
        },
        {
          id: "concepteurs-structure",
          label: lang === "en" ? "Data structure" : "Structure de données",
          // Vincent B982 (Eric 2026-02-26 SPEC STRICK PROD) — Réorganisation :
          //   Explorateur → Catégories → Recherche → Validation → Embeddings
          //   → Transcriptions → Locuteurs → Fusion locuteurs
          items: [
            { path: "/dashboard/explorer", icon: icons.Files, label: lang === "en" ? "File Explorer" : "Explorateur de fichiers", roles: R_CONCEPTEUR },
            { path: "/dashboard/categories", icon: icons.FolderOpen, label: lang === "en" ? "File categories" : "Catégories de fichiers", roles: R_CONCEPTEUR },
            { path: "/dashboard/search", icon: icons.MagnifyingGlass, label: lang === "en" ? "Search by tags" : "Recherche par tags", roles: R_CONCEPTEUR },
            { path: "/dashboard/data-validation", icon: icons.Eye, label: lang === "en" ? "Data validation" : "Validation des données", roles: R_CONCEPTEUR },
            { path: "/dashboard/admin/embeddings", icon: icons.Brain || icons.Sparkle, label: lang === "en" ? "Embeddings & RAG" : "Embeddings & RAG", roles: R_CONCEPTEUR, alwaysVisible: true },
            { path: "/dashboard/transcriptions", icon: icons.FileText || icons.Files, label: lang === "en" ? "Transcripts" : "Transcriptions", roles: R_CONCEPTEUR },
            // Tâche 1278 (Eric 2026-06-29) — Le tableau de bord des écarts
            // n'était visible QUE tout en haut de la page Transcriptions :
            // personne ne le trouvait. Il a son entrée.
            { path: "/dashboard/gaps", icon: icons.ChartBar || icons.FileText, label: lang === "en" ? "Transcript gaps" : "Écarts de transcription", roles: R_CONCEPTEUR },
            { path: "/dashboard/speakers", icon: icons.Microphone, label: lang === "en" ? "Speakers" : "Locuteurs", roles: R_CONCEPTEUR },
            // Vincent B982 (Eric 2026-02-26) — Fusion locuteurs REMONTÉ
            // depuis Admin > Technique vers Concepteurs > Structure de
            // données (juste après Locuteurs pour cohérence).
            { path: "/dashboard/admin/speaker-variants", icon: icons.UsersThree || icons.User, label: lang === "en" ? "Speaker variants" : "Fusion locuteurs", roles: R_CONCEPTEUR },
          ],
        },
        {
          id: "concepteurs-generation",
          label: lang === "en" ? "Knowledge generation" : "Génération de connaissances",
          // Vincent B982 (Eric 2026-02-26 SPEC STRICK PROD) — Ajout de
          // 5 entrées remontées depuis Admin > Technique (renommées) :
          //   Whisper : filtre agressif (ex-Filtre Whisper agressif)
          //   Whisper : groupage destructif (ex-Alertes pipeline)
          //   Maintenance Fireflies
          //   Corrections factuelles
          //   Procédures : regénération massive (nouvelle page B982)
          items: [
            { path: "/dashboard/concepteur/onboarding", icon: icons.Sparkle, label: lang === "en" ? "Designer Onboarding" : "Initiation concepteurs", roles: R_CONCEPTEUR, highlight: true, alwaysVisible: true },
            { path: "/dashboard/upload", icon: icons.Upload, label: t("upload_video"), roles: R_CONCEPTEUR },
            { path: "/dashboard/upload-text", icon: icons.FileText || icons.Upload, label: lang === "en" ? "Upload File" : "Importer un fichier", roles: R_CONCEPTEUR },
            { path: "/dashboard/video-queue", icon: icons.Queue, label: t("video_queue"), roles: R_CONCEPTEUR },
            { path: "/dashboard/admin/review", icon: icons.ClipboardText, label: t("review_queue"), roles: R_CONCEPTEUR },
            { path: "/dashboard/admin/aggressive-whisper-filter", icon: icons.Warning || icons.Bug || icons.Microphone, label: lang === "en" ? "Whisper: aggressive filter" : "Whisper : filtre agressif", roles: R_CONCEPTEUR },
            { path: "/dashboard/admin/alerts", icon: icons.Warning || icons.Bug, label: lang === "en" ? "Whisper: destructive grouping" : "Whisper : groupage destructif", roles: R_CONCEPTEUR },
            { path: "/dashboard/admin/fireflies-maintenance", icon: icons.Fire || icons.Bug, label: lang === "en" ? "Fireflies maintenance" : "Maintenance Fireflies", roles: R_CONCEPTEUR },
            { path: "/dashboard/admin/factual-corrections", icon: icons.CheckCircle || icons.Note, label: lang === "en" ? "Factual corrections" : "Corrections factuelles", roles: R_CONCEPTEUR },
            { path: "/dashboard/admin/procedures-regeneration", icon: icons.ClipboardText || icons.Play, label: lang === "en" ? "Procedures: bulk regeneration" : "Procédures : regénération massive", roles: R_CONCEPTEUR },
            { path: "/dashboard/admin/compliance-scoring", icon: icons.Gauge, label: t("scoring_amf"), roles: R_CONCEPTEUR },
          ],
        },
        // Vincent #185 (Eric 2026-05-27) — Accordéon "Parcours pédagogiques"
        // SUPPRIMÉ : Dictionnaire (édition) était un doublon avec /dashboard/dictionary
        // dans Connaissances ; Intégration (onboarding) Bientôt obsolète ;
        // Scoring AMF déplacé vers Génération de connaissances.
      ],
    },
    // ────────────────────────────────────────────────────────────────
    {
      id: "admin",
      label: lang === "en" ? "Admin" : "Admin",
      roles: R_ADMIN,
      sections: [
        {
          id: "admin-analytique",
          label: lang === "en" ? "Analytics" : "Analytique",
          items: [
            // Vincent #181 (Eric 2026-05-26 round 4) — Renommages :
            // "Analytique" → "Analytique du LMS" ; majuscules supprimées sur "Chatbot".
            { path: "/dashboard/admin/analytics", icon: icons.ChartLine, label: lang === "en" ? "LMS Analytics" : "Analytique du LMS", roles: R_ADMIN },
            { path: "/dashboard/admin/chatbot-sources", icon: icons.Robot, label: lang === "en" ? "Chatbot sources" : "Sources du chatbot", roles: R_ADMIN },
            { path: "/dashboard/admin/chatbot-gaps", icon: icons.Question, label: lang === "en" ? "Chatbot gaps" : "Lacunes du chatbot", roles: R_ADMIN },
          ],
        },
        {
          id: "admin-personnes",
          label: lang === "en" ? "People" : "Personnes",
          // Vincent #166 (Eric 2026-05-22) — Personnes = Équipe + Gestion tiers + Gestion rôles
          // Section "Gestion" (Entreprise + Partenaires) déplacée vers Plateforme Knoria > Personnes (cf. #166).
          items: [
            { path: "/dashboard/team-analytics", icon: icons.UsersThree, label: lang === "en" ? "Team" : "Équipe", roles: R_ADMIN },
            { path: "/dashboard/admin/tiers", icon: icons.UsersThree, label: lang === "en" ? "Tier Management" : "Gestion des tiers", roles: R_ADMIN },
            { path: "/dashboard/admin/role-labels", icon: icons.IdentificationCard || icons.UsersThree, label: lang === "en" ? "Role Management" : "Gestion des rôles", roles: R_ADMIN },
            { path: "/dashboard/admin/access-matrix", icon: icons.ShieldCheck || icons.UsersThree, label: lang === "en" ? "Access Selection" : "Sélection des accès", roles: R_ADMIN },
            { path: "/dashboard/admin/backups", icon: icons.Database || icons.CloudArrowUp || icons.ShieldCheck, label: lang === "en" ? "Backups" : "Sauvegardes", roles: R_ADMIN },
          ],
        },
        {
          id: "admin-integrations",
          // Eric 2026-06-04 — Nouvelle section dédiée "Intégrations & Services"
          // demandée explicitement par Eric pour rendre la config BYOK ultra-visible
          // (point d'entrée par entreprise, sous "Personnes").
          label: lang === "en" ? "Integrations & Services" : "Intégrations & Services",
          items: [
            { path: "/dashboard/settings/ai-package", icon: icons.Sparkle, label: lang === "en" ? "BYOK Configuration" : "Configuration BYOK", roles: R_ADMIN, highlight: true },
            { path: "/dashboard/admin/email-system-status", icon: icons.Envelope || icons.Sparkle, label: lang === "en" ? "Resend system status" : "Statut système Resend", roles: R_ADMIN },
          ],
        },
        {
          id: "admin-technique",
          label: lang === "en" ? "Technical" : "Technique",
          // Vincent B982 (Eric 2026-02-26 SPEC STRICK PROD) — Grand
          // ménage : Alertes pipeline, Maintenance Fireflies, Fusion
          // locuteurs, Corrections factuelles, Embeddings & RAG, Filtre
          // Whisper agressif et Historique debug DÉPLACÉS ou RETIRÉS.
          // Voir Concepteurs > Structure de données / Génération de
          // connaissances pour les entrées relocalisées.
          items: [
            { path: "/dashboard/admin/firewall", icon: icons.ShieldCheck, label: t("firewall_ai"), roles: R_ADMIN },
            { path: "/dashboard/platform/feature-flags", icon: icons.FlagIcon, label: lang === "en" ? "Feature Flags" : "Feature Flags", roles: R_ADMIN },
            { path: "/dashboard/privacy", icon: icons.ShieldCheck, label: lang === "en" ? "Law 25 / Data" : "Loi 25 / Données", roles: R_ADMIN },
            { path: "/dashboard/admin/diagnostics", icon: icons.Stethoscope || icons.Heartbeat || icons.Bug, label: lang === "en" ? "Diagnostics" : "Diagnostics", roles: R_ADMIN },
            { path: "/dashboard/admin/upload-maintenance", icon: icons.CloudArrowUp || icons.Database, label: lang === "en" ? "Upload maintenance" : "Maintenance uploads", roles: R_ADMIN },
          ],
        },
      ],
    },
    // ────────────────────────────────────────────────────────────────
    {
      id: "plateforme-knoria",
      label: lang === "en" ? "Platform Knoria" : "Plateforme Knoria",
      roles: R_FONDATEUR,
      crown: true,
      sections: [
        {
          id: "plateforme-overview",
          label: lang === "en" ? "Overview" : "Vue d'ensemble",
          // Vincent #184 (Eric 2026-05-27) — Vue d'ensemble SANS accordéon
          // (même pattern que Tableau de bord dans Général, alwaysVisible).
          hideLabel: true,
          items: [
            { path: "/dashboard/platform/overview", icon: icons.Crown, label: t("overview"), roles: R_FONDATEUR, alwaysVisible: true },
          ],
        },
        {
          id: "plateforme-personnes",
          label: lang === "en" ? "People" : "Personnes",
          items: [
            { path: "/dashboard/billing", icon: icons.Buildings, label: t("enterprise"), roles: R_FONDATEUR },
            { path: "/dashboard/billing/coupons", icon: icons.Sparkle, label: lang === "en" ? "Coupons" : "Coupons", roles: R_FONDATEUR },
            { path: "/dashboard/platform/approvals", icon: icons.UserCirclePlus, label: lang === "en" ? "Approvals" : "Demandes d'accès", roles: R_FONDATEUR },
            { path: "/dashboard/platform/organizations", icon: icons.Buildings, label: lang === "en" ? "Organization management" : "Gestion des organisations", roles: R_FONDATEUR },
            { path: "/dashboard/admin/partners", icon: icons.Handshake, label: t("partners"), roles: R_FONDATEUR },
          ],
        },
        {
          id: "plateforme-technique",
          label: lang === "en" ? "Technical" : "Technique",
          // Vincent #328 (Eric 2026-06-17) — "Suivi des bogues" RETIRÉ (lien + page).
          items: [
            { path: "/dashboard/admin/tech-stack", icon: icons.Stack, label: lang === "en" ? "Technical configuration" : "Configuration technique", roles: R_FONDATEUR },
            // B1053 (Eric 2026-09-04) — Sentinelle Connexion : sonde un vrai
            // login toutes les 5 min + canari de transport navigateur. Créée
            // après l'incident 2026-09-02/04 (2 jours sans connexion possible,
            // zéro alerte car /api/health répondait 200).
            { path: "/dashboard/admin/login-sentinel", icon: icons.Heartbeat || icons.Stethoscope, label: lang === "en" ? "Login Sentinel" : "Sentinelle connexion", roles: R_FONDATEUR },
            // B1055 (Eric 2026-09-04) — Journal d'incidents : chaque panne
            // avec sa cause réelle et son correctif. Pré-rempli avec
            // l'historique B1040 → B1053.
            { path: "/dashboard/admin/incidents", icon: icons.Warning || icons.Stack, label: lang === "en" ? "Incident journal" : "Journal d'incidents", roles: R_FONDATEUR },
            // B1067 (Eric 2026-09-04) — Historique des vérifications
            // factuelles : qui a vérifié quoi, ce qui a été trouvé, et les
            // synthèses modifiées depuis (B1068).
            { path: "/dashboard/admin/synthesis-verifications", icon: icons.MagnifyingGlass || icons.Stack, label: lang === "en" ? "Synthesis verifications" : "Vérifications des synthèses", roles: R_FONDATEUR },
            { path: "/dashboard/platform/audit-logs", icon: icons.AuditIcon, label: lang === "en" ? "Audit logs" : "Logs d'audit", roles: R_FONDATEUR },
          ],
        },
      ],
    },
  ];
}

/**
 * Filter pillars > sections > items based on the user's effective role.
 * Returns only what's accessible (and removes empty sections/pillars).
 * Items with `comingSoon: true` are kept (rendered grisés).
 *
 * Vincent #198 (Eric 2026-05-28) — Applique aussi les overrides org-spécifiques
 * stockés dans `org.role_access_overrides[role]`.
 *
 * @param pillars - structure brute issue de buildMenuConfig
 * @param role    - org_role effectif de l'utilisateur (ex: "employee_trained")
 * @param hiddenPaths - Set<string> de paths cachés pour ce rôle (issu de /api/org/access-matrix)
 */
export function filterMenuForRole(pillars, role, hiddenPaths = null) {
  const hidden = hiddenPaths instanceof Set ? hiddenPaths : new Set(hiddenPaths || []);
  return pillars
    .filter((p) => !p.roles || p.roles.includes(role))
    .map((p) => ({
      ...p,
      sections: (p.sections || [])
        .map((s) => ({
          ...s,
          items: (s.items || []).filter((i) => {
            if (i.roles && !i.roles.includes(role)) return false;
            if (hidden.has((i.path || "").replace(/\/$/, ""))) return false;
            return true;
          }),
        }))
        .filter((s) => s.items.length > 0),
    }))
    .filter((p) => p.sections.length > 0);
}
